import { ComplexValue, LoadOptions, Value } from 'app/types-shared';
import { OptionsRequestParams, OptionsRequestContext } from '../ui/form/types';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';
import {
  ComponentContext, ComponentDescriptor,
  ChildComponentContext
} from 'app/infrastructure/ui/types-uischema';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

/**
 * Maps the Code table value item from JSON wire format to a client store value object
 *
 * @param code The Code Table item to import
 */
export const codeItemToStoreValue = (code: CodetableWireFormat): ComplexValue<any> => {

  if (!code) {
    return undefined;
  }

  const rawValue: ComplexValue<number> = {
    value: code.v,
    description: code.d,
    longDescription: code.ld,
    allLevelDescription: code.ald,
    backgroundColor: code.b,
    foregroundColor: code.f,
    icon: code.i && toAbsoluteUrl(code.i),
    descendants: !!code.children ? code.children.map(codeItemToStoreValue) : undefined,
    minLevel: code.ml,
    categoryId: code.ctg,
    extra: code.extra,
    owner: !!code.owner ? codeItemToStoreValue(code.owner) : undefined,
    sharedThroughUserGroup: code.sharedThroughUserGroup,
    bookmark: code.bookmark
  };

  const value = Object.keys(rawValue).reduce((ret, key) => {
    if (rawValue[key] !== undefined) {
      ret[key] = rawValue[key];
    }
    return ret;
  }, {} as ComplexValue<any>);

  return Object.keys(value).length ? value : undefined;
};

/**
 * Maps from a client store value back to the JSON wire format
 * 
 * @param storeValue The raw value from store
 */
export const storeValueToCodeItem = (storeValue: ComplexValue<any>): CodetableWireFormat => {

  if (!storeValue) {
    return undefined;
  }

  const rawValue: CodetableWireFormat = {
    v: storeValue?.value,
    d: storeValue?.description,
    ld: storeValue?.longDescription,
    ald: storeValue?.allLevelDescription,
    b: storeValue?.backgroundColor,
    f: storeValue?.foregroundColor,
    i: storeValue?.icon,
    children: storeValue?.descendants?.map(storeValueToCodeItem),
    ml: storeValue?.minLevel,
    ctg: storeValue?.categoryId,
    extra: storeValue?.extra as any,
    owner: !!storeValue?.owner ? storeValueToCodeItem(storeValue.owner) : undefined,
    sharedThroughUserGroup: storeValue?.sharedThroughUserGroup,
    bookmark: storeValue?.bookmark
  };

  const value = Object.keys(rawValue).reduce((ret, key) => {
    if (rawValue[key] !== undefined) {
      ret[key] = rawValue[key];
    }
    return ret;
  }, {} as CodetableWireFormat);

  return Object.keys(value).length ? value : undefined;
};

export const mapToCodeListItemWire = (results: { decimalColour: number, code: Value, category?: Value, descendantCount?: number }[]) =>
  results?.map(item => ({
    ...(isDefinedIncludingZero(item?.decimalColour) && { b: item?.decimalColour }),
    ...(isDefinedIncludingZero(item?.code?.v) && { v: item?.code?.v }),
    ...(item?.code?.d && { d: item?.code?.d }),
    ...(isDefinedIncludingZero(item?.category?.v) && { category: { value: item?.category?.v, description: item?.category?.d } }),
    ...(isDefinedIncludingZero(item?.descendantCount) && { descendantCount: item.descendantCount })
  }));

export const mapToOptionsRequestPayload = (value: { query: string, limitFieldsTo?: string[] }) => {
  return {
    ...(value?.query && {
      query: {
        text: value.query
      }
    }),
    ...(value?.limitFieldsTo && { limitFieldsTo: value?.limitFieldsTo })
  };
};

export const mapToOptionsRequestParams = (loadOptions: LoadOptions): OptionsRequestParams => {
  const {
    sort, query, apiVersion, parentId, siblingId, levels, categoryId, displayNotSelected, iteratorId, optionsCollectionId, consequenceType,
    indicatorSubstanceId, substanceId, leafNodesOnly
  } = (loadOptions || {} as LoadOptions);

  return {
    sortDirection: sort ? sort : undefined,
    query: !!query && query.trim() ? query.trim() : null,
    ...(apiVersion && { 'api-version': apiVersion }),
    parentId,
    siblingId,
    levels,
    categoryId,
    displayNotSelected,
    iteratorId,
    optionsCollectionId,
    consequenceType,
    indicatorSubstanceId,
    substanceId,
    leafNodesOnly
  };
};

export const mapToOptionsRequestContext = (
  context: ComponentContext | ChildComponentContext, descriptor: ComponentDescriptor, loadOptions: LoadOptions
): OptionsRequestContext => {

  return {
    applicationId: context.applicationId,
    path: context.path,
    domainId: (loadOptions && loadOptions.domainId) || descriptor.id  // default to the descriptor domain id.
  };
};
